import { useState } from "react";
import { ChevronDown, CheckCircle2, TrendingUp, Zap } from "lucide-react";
import BlogLayout from "@/components/BlogLayout";

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-stone rounded-2xl overflow-hidden not-prose mb-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left bg-white hover:bg-bone-soft transition-colors"
        style={{ touchAction: "manipulation" }}
      >
        <span className="font-semibold text-charcoal text-sm sm:text-base leading-snug">{q}</span>
        <ChevronDown
          className={`w-4 h-4 text-charcoal-muted shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-3 bg-white text-charcoal-muted text-sm sm:text-base leading-relaxed border-t border-stone">
          {a}
        </div>
      )}
    </div>
  );
}

const ANALYSIS_STEPS = [
  {
    step: "1",
    title: "Define your target role clearly",
    body: "\"Senior Software Engineer at a product company\" is too vague. Pick 3 specific job postings from companies in your target band — ideally ones you'd actually apply to. These JDs are your ground truth.",
  },
  {
    step: "2",
    title: "Extract the required skills from each JD",
    body: "List every skill, technology, and experience requirement mentioned. Group them into: technical skills, soft skills, leadership expectations, domain knowledge. Note how often each appears across all 3 JDs — frequency signals importance.",
  },
  {
    step: "3",
    title: "Rate your current level honestly",
    body: "For each required skill, rate yourself: Not started / Beginner / Intermediate / Advanced. Be brutally honest — interview rejections are expensive feedback. Ask a peer or manager to sanity-check your self-assessment.",
  },
  {
    step: "4",
    title: "Prioritise gaps by impact × learning time",
    body: "A skill that appears in all 3 JDs and you're at Beginner level is your highest priority. A skill that appears once and you're at Intermediate is low priority. Build a simple matrix: (JD frequency × 3) + (current gap × 2) = priority score.",
  },
  {
    step: "5",
    title: "Build a time-boxed learning plan",
    body: "For each high-priority gap: identify the learning resource, estimate hours to reach 'Intermediate', and book the time in your calendar. Don't plan more than 2 skills simultaneously — context-switching kills retention.",
  },
  {
    step: "6",
    title: "Create evidence of progress",
    body: "Learning without output is invisible to recruiters. For each skill you close, create an artifact: a GitHub project, a technical blog post, a case study, or a certification. This is what goes on your resume and what you discuss in interviews.",
  },
];

const ROLE_GAPS = [
  {
    transition: "Mid-level SWE → Senior SWE",
    technical: "System design (HLD/LLD), code review patterns, performance optimisation, observability",
    nonTechnical: "Technical mentorship, cross-team coordination, architectural decision-making",
    timeToClose: "6–12 months",
    salaryImpact: "+₹8–25L CTC",
  },
  {
    transition: "Senior SWE → Staff/Principal",
    technical: "Large-scale distributed systems, platform thinking, multi-team API design",
    nonTechnical: "Engineering strategy, influence without authority, roadmap alignment with business goals",
    timeToClose: "12–24 months",
    salaryImpact: "+₹15–50L CTC",
  },
  {
    transition: "Engineer → PM (APM/PM)",
    technical: "Product analytics, A/B testing fundamentals, SQL for product metrics",
    nonTechnical: "User research, PRD writing, stakeholder management, prioritisation frameworks",
    timeToClose: "6–12 months",
    salaryImpact: "Lateral or small step-up initially; +₹15–30L after 2 years",
  },
  {
    transition: "Junior DE → Senior DE",
    technical: "Stream processing (Kafka/Flink), dbt, data lakehouse patterns, query optimisation at scale",
    nonTechnical: "Data contracts, cross-team data modelling, incident response",
    timeToClose: "9–18 months",
    salaryImpact: "+₹10–30L CTC",
  },
];

const FAQS = [
  {
    q: "How is a skill gap analysis different from just looking at a job description?",
    a: "A JD tells you what a company wants. A skill gap analysis tells you the delta between what you have and what's needed — and quantifies it so you can prioritise what to learn first. Most people read JDs passively; a skill gap analysis forces you to make an honest self-assessment and build a plan.",
  },
  {
    q: "How often should I redo the analysis?",
    a: "Quarterly is ideal if you're actively targeting a transition. Annually if you're in a stable role and focused on deep skill development. After any significant industry shift (e.g., AI becoming a core expectation in your domain), redo it immediately.",
  },
  {
    q: "What if I don't know what skills I'm missing?",
    a: "This is exactly where most people get stuck. The best shortcut is to find 2–3 people who are already in your target role and ask them: 'What did you wish you'd known or built before making this transition?' LinkedIn outreach to second-degree connections with a specific question usually gets a 30–40% response rate.",
  },
  {
    q: "Should I go deep on one skill or broad across many?",
    a: "Depends on where you are in your career. Early career (0–4 years): go broad — you need a working foundation across the full stack of your domain. Mid-career (4–8 years): go deep on 1–2 skills that differentiate you in your target market. Senior+ (8+ years): go broad again, but at a strategic level — understanding adjacent domains is what makes you influential.",
  },
  {
    q: "Can I do a skill gap analysis myself, or do I need a tool?",
    a: "You can do a basic version yourself with a spreadsheet. A tool like Pathvio helps by matching your resume to actual JDs and surfacing gaps you might not have noticed — particularly around how you phrase experience (e.g., you have the skill but your resume doesn't signal it in a way that matches how JDs are written).",
  },
];

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  })),
};

export default function SkillGapTechCareer() {
  return (
    <BlogLayout
      title="Skill Gap Analysis for Your Tech Career in India (2026 Guide) | Pathvio"
      description="A practical framework for identifying and closing skill gaps in your Indian tech career — covering how to run the analysis, common gaps by role transition, and how to build evidence of progress that recruiters actually notice."
      slug="skill-gap-analysis-tech-career"
      publishedDate="2026-05-21"
      readingTime="8 min read"
      category="Career Development"
      ctaHeading="Get your personalised skill gap report"
      ctaBody="Pathvio analyses your resume against your target role and builds a prioritised gap report — showing exactly what to learn, in what order, to move to the next salary band."
      faqSchema={FAQ_SCHEMA}
    >

      <h1>Skill Gap Analysis for Your Tech Career in India: A Practical 2026 Guide</h1>

      {/* TL;DR — key takeaways for quick reference and GEO citation */}
      <div className="not-prose my-8 bg-moss/8 border border-moss/20 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Zap className="w-4 h-4 text-moss shrink-0" />
          <span className="text-xs font-bold uppercase tracking-widest text-moss">TL;DR</span>
        </div>
        <ul className="space-y-2 text-sm text-charcoal">
          {[
            "A skill gap analysis maps the delta between your current skills and what your target role requires — done right, it takes under 2 hours.",
            "Mid-level to Senior SWE typically needs 6–12 months to close; Senior to Staff/Principal takes 12–24 months.",
            "System design (HLD/LLD), observability, and technical mentorship are the top gaps for SWEs targeting Senior roles in India.",
            "Closing a gap without visible output (GitHub, blog post, cert) is invisible to recruiters — always create an artefact.",
            "The fastest path: identify 3 real JDs at your target company, extract the required skills, then gap-rate yourself honestly.",
          ].map((point) => (
            <li key={point} className="flex items-start gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-moss mt-0.5 shrink-0" />
              <span>{point}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Stats block — GEO citable */}
      <div className="not-prose grid grid-cols-2 gap-3 my-8">
        {[
          { stat: "6–12 mo", label: "typical time to close skill gaps for mid-level to senior SWE transition", source: "" },
          { stat: "3 JDs", label: "analysed is the minimum to accurately identify which skills matter most for your target role", source: "" },
        ].map((s) => (
          <div key={s.stat} className="bg-bone-soft border border-stone rounded-2xl p-4">
            <div className="font-heading text-2xl font-bold text-moss mb-1">{s.stat}</div>
            <div className="text-xs text-charcoal-muted leading-relaxed">{s.label}</div>
            {s.source && <div className="text-[10px] text-charcoal-muted/50 mt-1.5 italic">{s.source}</div>}
          </div>
        ))}
      </div>

      <p>
        Most professionals know they need to "upskill" — but few have a clear map of exactly
        which skills to focus on, in what order, and how to prove they've closed the gap to a
        recruiter. A skill gap analysis converts the vague anxiety of "am I falling behind?" into
        a concrete, prioritised action list.
      </p>

      <p>
        This guide shows you how to run one in a single afternoon, and what the most common gaps
        look like for the transitions Indian tech professionals most frequently target.
      </p>

      <hr />

      <h2>What is a skill gap analysis?</h2>

      <p>
        A skill gap analysis is a structured comparison between the skills your target role requires
        and the skills you currently have. The output is a prioritised list of gaps — ranked by
        how much closing each gap will improve your candidacy.
      </p>

      <p>
        It's not the same as a learning wishlist. Most people's "upskilling plans" are just
        optimistic lists of courses they want to take someday. A skill gap analysis forces you to
        be specific about:
      </p>

      <ul>
        <li>Which role, at which type of company, at which seniority level you're targeting</li>
        <li>Which specific skills that role requires</li>
        <li>Where you actually stand on each skill today</li>
        <li>Which gaps are blocking you most</li>
      </ul>

      <p>
        Done well, it replaces months of unfocused learning with a targeted 90-day sprint on the
        2–3 things that will move your candidacy the most.
      </p>

      <hr />

      <h2>The 6-step framework</h2>

      <div className="not-prose space-y-4 my-6">
        {ANALYSIS_STEPS.map((s) => (
          <div key={s.step} className="flex gap-4 bg-white border border-stone rounded-2xl p-5">
            <div className="w-8 h-8 rounded-full bg-moss text-bone text-sm font-bold flex items-center justify-center shrink-0 mt-0.5">
              {s.step}
            </div>
            <div>
              <h3 className="font-heading font-semibold text-charcoal text-base mb-2">{s.title}</h3>
              <p className="text-sm text-charcoal-muted leading-relaxed">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <hr />

      <h2>Common skill gaps by role transition in Indian tech (2026)</h2>

      <p>
        Based on JD analysis and community feedback, here are the most frequently cited gaps
        for four high-demand transitions in the Indian tech market:
      </p>

      <div className="not-prose space-y-4 my-6">
        {ROLE_GAPS.map((gap) => (
          <div key={gap.transition} className="bg-white border border-stone rounded-2xl overflow-hidden">
            <div className="bg-bone-soft border-b border-stone px-5 py-3 flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading font-semibold text-charcoal">{gap.transition}</h3>
              <div className="flex gap-3 text-xs">
                <span className="bg-moss/10 text-moss rounded-full px-3 py-1 font-semibold">{gap.salaryImpact}</span>
                <span className="bg-stone text-charcoal-muted rounded-full px-3 py-1">{gap.timeToClose}</span>
              </div>
            </div>
            <div className="p-5 grid sm:grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-charcoal-muted mb-2">Technical gaps</p>
                <p className="text-sm text-charcoal-muted leading-relaxed">{gap.technical}</p>
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-charcoal-muted mb-2">Non-technical gaps</p>
                <p className="text-sm text-charcoal-muted leading-relaxed">{gap.nonTechnical}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <hr />

      <h2>How to make closed gaps visible on your resume</h2>

      <p>
        Learning without evidence is invisible. When a recruiter screens your resume, they have
        ~30 seconds — there's no way to communicate "I studied this for 3 months" without a visible
        artifact. Here's what actually works:
      </p>

      <h3>GitHub projects (technical roles)</h3>
      <p>
        Build something small but real. A system design implementation (a rate limiter, a URL
        shortener, a message queue) pinned on your GitHub profile gives interviewers something
        concrete to discuss and signals genuine interest beyond coursework.
      </p>

      <h3>Technical writing</h3>
      <p>
        Write a blog post explaining a concept you've learned — on Medium, dev.to, or a personal
        site. The act of explaining it forces genuine understanding, and the published piece is
        a resume link. One strong technical article in your target domain has more signal than
        five certifications.
      </p>

      <h3>Open source contributions</h3>
      <p>
        Even small contributions (documentation, bug fixes, test coverage) to a well-known project
        signal that you write code that other engineers will read — a meaningful differentiator
        for senior roles.
      </p>

      <h3>Certifications — used correctly</h3>
      <p>
        Certifications are weak signals on their own, but AWS Solutions Architect, GCP Professional,
        or dbt Analytics Engineering are genuinely useful evidence of a specific skill set.
        Add them if they're directly relevant to the gap you closed; skip them if they're
        tangential to your target role.
      </p>

      <hr />

      <h2>The AI skill gap: what to do about it</h2>

      <p>
        Every 2026 JD analysis reveals the same new pattern: AI/ML fluency is now an expected
        baseline for most tech roles, not a differentiator. "Experience with LLMs" appears in
        software engineer, product manager, and data engineer JDs with roughly equal frequency.
      </p>

      <p>
        This doesn't mean you need to become an ML engineer. It means:
      </p>

      <ul>
        <li><strong>SWEs:</strong> Know how to integrate LLM APIs (OpenAI, Claude, Gemini), prompt engineering basics, and RAG architecture patterns.</li>
        <li><strong>PMs:</strong> Understand AI product limitations (hallucinations, latency, cost), know how to write AI product requirements, and be able to evaluate AI feature tradeoffs.</li>
        <li><strong>DEs:</strong> Know vector databases, embedding pipelines, and how ML models consume data — not to build them, but to build the infrastructure that feeds them.</li>
      </ul>

      <blockquote>
        The fastest way to build AI fluency in 2026: build one real thing with an LLM API. Not a
        tutorial — a side project you'd actually use. The learning density of building vs.
        watching is roughly 10x.
      </blockquote>

      <hr />

      <h2>Frequently asked questions</h2>

      <div className="not-prose mt-6">
        {FAQS.map((faq) => (
          <FaqItem key={faq.q} q={faq.q} a={faq.a} />
        ))}
      </div>

      <hr />

      <h2>Related guides</h2>
      <ul>
        <li>
          <a href="/salary/software-engineer-india">Software Engineer Salary in India (2026)</a>
          {" "}— see the exact salary uplift from closing the Senior → Staff gap.
        </li>
        <li>
          <a href="/blog/product-manager-india-no-mba">How to Become a PM Without an MBA</a>
          {" "}— the specific skill gaps for the engineer-to-PM transition, in detail.
        </li>
        <li>
          <a href="/blog/naukri-resume-score-explained">Your Naukri Resume Score, Explained</a>
          {" "}— make sure the skills you've closed are visible to recruiters.
        </li>
        <li>
          <a href="/blog/top-ai-roles-in-india-career-guide">Top AI Roles in India: Jobs, Salaries, Skills &amp; How to Break In</a>
          {" "}— if AI/ML is the direction you're closing the gap toward, the role-by-role breakdown.
        </li>
        <li>
          <a href="/blog/resume-mistakes-india">14 Resume Mistakes That Get You Rejected</a>
          {" "}— closing a skill gap only helps if the resume showing it is read properly in the first place.
        </li>
      </ul>

    </BlogLayout>
  );
}
